import { message } from 'antd'
import { useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useCallback, useEffect, useRef, useState } from 'react'

interface UseAnnotationScreenshotCaptureProps {
  annotationId: string
  kestrelModelId?: string
  onSuccess?: () => void
}

interface UseAnnotationScreenshotCaptureResult {
  isCapturing: boolean
  showMeasurements: boolean
  setShowMeasurements: (value: boolean) => void
  showLabels: boolean
  setShowLabels: (value: boolean) => void
  showWidgets: boolean
  setShowWidgets: (value: boolean) => void
  showAnnotationMarkers: boolean
  setShowAnnotationMarkers: (value: boolean) => void
  optionsExpanded: boolean
  setOptionsExpanded: (value: boolean) => void
  handleTakeScreenshot: () => Promise<void>
}

export const useAnnotationScreenshotCapture = ({
  annotationId,
  kestrelModelId,
  onSuccess,
}: UseAnnotationScreenshotCaptureProps): UseAnnotationScreenshotCaptureResult => {
  const [isCapturing, setIsCapturing] = useState(false)
  const [showMeasurements, setShowMeasurements] = useState(true)
  const [showLabels, setShowLabels] = useState(true)
  const [showWidgets, setShowWidgets] = useState(true)
  const [showAnnotationMarkers, setShowAnnotationMarkers] = useState(true)
  const [optionsExpanded, setOptionsExpanded] = useState(false)
  const [requestId, setRequestId] = useState('')

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const context = useKestrelViewerContext()
  const contextRef = useRef(context)
  contextRef.current = context

  const { sendCommand, setScreenshotResult } = context

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const currentScreenshotResult = context.screenshotResult

    if (
      !currentScreenshotResult ||
      !requestId ||
      currentScreenshotResult.requestId !== requestId
    ) {
      return
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    setScreenshotResult(null)

    if (currentScreenshotResult.success) {
      message.success('Screenshot captured successfully!')
      onSuccess?.()
    } else {
      message.error('Screenshot capture failed')
    }

    setIsCapturing(false)
    setRequestId('') // Clear request ID to prevent reprocessing
  }, [context.screenshotResult, requestId, setScreenshotResult, onSuccess])

  const handleTakeScreenshot = useCallback(async () => {
    if (!kestrelModelId || !sendCommand || !setScreenshotResult) {
      console.error('Missing required context for screenshot capture')
      message.error('Screenshot capture unavailable. Please try again.')
      return
    }

    setIsCapturing(true)
    setScreenshotResult(null)

    const newRequestId = `annotation_${annotationId}_${Date.now()}`
    setRequestId(newRequestId)

    sendCommand({
      command: KestrelCommands.TAKE_SCREENSHOT,
      requestId: newRequestId,
      entryId: kestrelModelId,
      annotationId,
      showMeasurements,
      showLabels,
      showWidgets,
      showAnnotationMarkers,
    })

    timeoutRef.current = setTimeout(() => {
      message.error('Screenshot capture failed or timed out')
      setIsCapturing(false)
      setRequestId('')
      timeoutRef.current = null
    }, 10000)
  }, [
    kestrelModelId,
    sendCommand,
    setScreenshotResult,
    annotationId,
    showMeasurements,
    showLabels,
    showWidgets,
    showAnnotationMarkers,
  ])

  return {
    isCapturing,
    showMeasurements,
    setShowMeasurements,
    showLabels,
    setShowLabels,
    showWidgets,
    setShowWidgets,
    showAnnotationMarkers,
    setShowAnnotationMarkers,
    optionsExpanded,
    setOptionsExpanded,
    handleTakeScreenshot,
  }
}
